












































































































































import Vue from 'vue';
import { AbsenceType } from '@/class/common/const/labor-const';
import { CalEvent } from '@/class/models/views/labor';
import { LaborCalendarEventKind } from '@/class/models/typed/labor';
import DatePicker from 'vuejs-datepicker';

import { ja } from 'vuejs-datepicker/dist/locale';

const toDate = (v: string) => new Date(v);
// const pad2 = (n: number) => String(n).padStart(2, "0");

function parseStartTimeFromString(e: CalEvent): string {
  if (e.startTime) {
    return e.startTime; // 明示時刻があればそれを優先
  }

  if (typeof e.start === 'string') {
    // 純粋な日付だけなら時刻なし
    if (/^\d{4}-\d{2}-\d{2}$/.test(e.start)) {
      return '';
    }
    // ISO日時 'YYYY-MM-DDTHH:mm' から HH:mm を抽出
    const m = e.start.match(/T(\d{2}):(\d{2})/);
    if (m) {
      return `${m[1]}:${m[2]}`;
    }
    return '';
  }

  return '';
}

// 経過分数に変換（時刻無しは無限大）
function toMinutes(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
  if (!m) {
    return Number.POSITIVE_INFINITY; // 時刻無しは最後に回す
  }
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

// UI表示用のテキスト
function buildDisplayText(e: any): string {
  // カスタム休日なら name をそのまま表示
  if (e.isCustomHoliday) {
    return (e.workContent || '休日').trim();
  }
  switch (e.kind) {
    case LaborCalendarEventKind.LATE:
      return e.startTime ? `${e.startTime} 遅刻・早退` : '遅刻・早退';
    case LaborCalendarEventKind.EARLY:
      return e.startTime ? `${e.startTime} 遅刻・早退` : '遅刻・早退';
    case LaborCalendarEventKind.HOLIDAY_FULL:
      // absenceType により有給・特別休暇などを判定
      if (e.absenceType === AbsenceType.PAID_LEAVE) {
        return '有給(全日)';
      }
      if (e.absenceType === AbsenceType.SPECIAL_LEAVE) {
        return e.specialLeaveName || '休暇(全日)';
      }
    case LaborCalendarEventKind.HOLIDAY_HALF:
      return `${e.startTime} 有給(半日) `;
    case LaborCalendarEventKind.ABSENCE:
      return '欠勤';
    default:
      // 業務内容
      return (e.workContent || '').trim();
  }
}

function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}
function isSameDate(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function addMonths(d: Date, n: number) {
  const x = new Date(d);
  x.setDate(1);
  x.setMonth(x.getMonth() + n);
  return x;
}
function getMonthMatrix(viewDate: Date) {
  const weekStart = 0; // 0=Sun 固定
  const y = viewDate.getFullYear();
  const m = viewDate.getMonth();

  const first = new Date(y, m, 1); // 月初
  const last = new Date(y, m + 1, 0); // 月末

  // 週頭からのズレを計算(負の値になることがある為、+7してから%7)
  const firstDow = (first.getDay() - weekStart + 7) % 7;
  const start = new Date(y, m, 1 - firstDow);

  // 週の開始曜日から見た、月末日の“列インデックス”(0〜6)
  const lastDow = (last.getDay() - weekStart + 7) % 7;
  // その週の最終列（インデックス6）まで、あと何日足りないか
  const daysAfter = 6 - lastDow;
  const end = new Date(last);
  // カレンダー表示の最終日
  end.setDate(last.getDate() + daysAfter);
  const out: Date[] = [];
  for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d));
  }
  return out; // 7の倍数個（28/35/42）
}

// 指定のセルの日付が、イベントの期間 [start..end] に含まれるか（end省略時は単一日扱い）
function isDateInInclusiveRange(date: Date, rangeStart: Date, rangeEnd?: Date) {
  const dayMs = startOfDay(date).getTime();
  const startMs = startOfDay(rangeStart).getTime();
  const endMs = rangeEnd ? startOfDay(rangeEnd).getTime() : startMs;
  return dayMs >= startMs && dayMs <= endMs;
}

function addDays(d: Date, n: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export default Vue.extend({
  props: {
    // 親からイベントを渡す想定
    events: {
      type: Array,
      default: () => [],
    },
    initialDate: {
      type: Date,
      required: true,
    },
  },
  components: {
    DatePicker,
  },
  watch: {
    viewDate: {
      immediate: true,
      handler() {
        this.emitRangeChange();
        this.$nextTick(() => this.recalcMaxRows());
      },
    },
  },
  data() {
    const now = new Date();
    return {
      DatePickerMonth: 'yyyy. M',
      ja,
      today: startOfDay(new Date()),
      viewDate: this.initialDate,
      monthOptions: [] as Array<{ value: string; label: string }>,
      weekdays: ['日', '月', '火', '水', '木', '金', '土'],
      maxRowsPerCell: 4, // 計測後に上書き
    };
  },
  created() {
    this.buildMonthOptions();
  },
  mounted() {
    this.$nextTick(() => {
      // 表示行数計算
      this.recalcMaxRows();
      const grid = this.$refs.bodyGrid as HTMLElement | undefined;
      if (!grid) {
        return;
      }
      // リサイズで再計測
      const ro = new ResizeObserver(() => this.recalcMaxRows());
      // カレンダーのgridを監視
      ro.observe(grid);
      // 後で解放するために保持
      (this as any)._ro = ro;
    });
  },
  beforeDestroy() {
    const ro = (this as any)._ro as ResizeObserver | undefined;
    if (ro && typeof ro.disconnect === 'function') {
      ro.disconnect();
    }
  },
  computed: {
    canGoNext(): boolean {
      const currentMonth = new Date(
        this.today.getFullYear(),
        this.today.getMonth(),
        1,
      );
      const viewMonth = new Date(
        (this.viewDate as any).getFullYear(),
        (this.viewDate as any).getMonth(),
        1,
      );
      return viewMonth < currentMonth;
    },
    weekCount(): number {
      // 日曜始まりの可変週 （月によって4〜6週）
      return getMonthMatrix(this.viewDate as any).length / 7;
    },
    calDaysStyle(): Record<string, string> {
      return {
        // 週数分の行を均等割り
        gridTemplateRows: `repeat(${this.weekCount}, 1fr)`,
        // カレンダー本体の高さ
        height: 'clamp(420px, 80vh, 720px)',
      };
    },
    cells(): Array<{
      key: string;
      date: Date;
      monthOffset: number;
      events: any[];
    }> {
      const daysInView = getMonthMatrix(this.viewDate as any);
      const viewYear = (this.viewDate as any).getFullYear();
      const viewMonth = (this.viewDate as any).getMonth();
      const firstOfMonth = new Date(viewYear, viewMonth, 1);
      // うるう年ならtrue、違えばfalse
      const isLeap = (y: number) => new Date(y, 1, 29).getMonth() === 1;
      // 年次イベントの日付の年部分を置換。2/29は、差し替え年がうるう年でなければ 2/28 にする
      const replaceYear = (ymd: string, y: number) => {
        const [yy, mm, dd] = ymd.split('-');
        const m = Number(mm);
        const  d = Number(dd);
        if (m === 2 && d === 29 && !isLeap(y)) {
          return `${y}-02-28`;
        }
        return `${y}-${mm}-${dd}`;
      };

      // everyYear:true なイベントを今見ている年（viewYear）に写しかえたコピーを作る
      const expanded: CalEvent[] = (this.events as CalEvent[]).flatMap(ev => {
        if (!ev.everyYear) {
          return [ev];
        }

        const originalYear = Number(ev.start.slice(0, 4)); // 元の年（例: 2025）
        // 月カレンダーは前月・翌月の日も1〜2列ぶん滲み出るので、viewYear の前後1年（±1）も作っておく。
        return [viewYear - 1, viewYear, viewYear + 1]
          .filter(y => y >= originalYear) // 元の年より前は表示しない
          .map(y => ({
            ...ev,
            start: replaceYear(ev.start, y),
            end: ev.end ? replaceYear(ev.end, y) : undefined,
          }));
      });
      // イベントを内部用に正規化
      const normalizedEvents = expanded.map((event, idx) => {
        const startDate = toDate(event.start);
        const endDate = event.end ? toDate(event.end) : undefined;

        const kind = event.kind;
        const startTime = parseStartTimeFromString(event);
        const isCustomHoliday = event.isCustomHoliday || false;

        const base = {
          ...event,
          kind,
          startDate,
          endDate,
          startTime,
          isCustomHoliday,
        };
        const displayText = buildDisplayText(base);

        return {
          ...base,
          displayText,
          __k:
            event.id != null
              ? String(event.id)
              : `${startDate.getTime()}|${idx}`,
        };
      });

      return daysInView.map(dateInCell => {
        const isCurrentMonth = dateInCell.getMonth() === viewMonth;
        const monthOffset = isCurrentMonth
          ? 0
          : dateInCell < firstOfMonth
          ? -1
          : 1;

        // その日のイベントを抽出
        const eventsForTheDay = normalizedEvents.filter(event => {
          // まず期間内か
          if (
            !isDateInInclusiveRange(dateInCell, event.startDate, event.endDate)
          ) {
            return false;
          }
          // 終了時刻が24:00の場合、翌日には表示しない
          if (
          (
            event.kind === LaborCalendarEventKind.WORK ||
            event.kind === LaborCalendarEventKind.LATE ||
            event.kind === LaborCalendarEventKind.EARLY ||
            event.kind === LaborCalendarEventKind.HOLIDAY_HALF
          ) &&
          !isSameDate(event.startDate, dateInCell)
        ) {
          return false;
        }
          return true;
        });
        const key = [
          dateInCell.getFullYear(),
          String(dateInCell.getMonth() + 1).padStart(2, '0'),
          String(dateInCell.getDate()).padStart(2, '0'),
        ].join('-');

        return { key, date: dateInCell, monthOffset, events: eventsForTheDay };
      });
    },
  },
  methods: {
    isCellClickable(cell: { date: Date; monthOffset: number; events: any[] }) {
      return !this.isFutureDate(cell.date);
    },
    isWeekend(date: Date) {
      const day = date.getDay();
      return day === 0 || day === 6;
    },

    isFutureDate(date: Date) {
      return startOfDay(date) > this.today;
    },
    isoDate(d: Date) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    formatDay(d: Date) {
      return `${d.getDate()}日`;
    },
    labels(cell: { date: Date; events: any[] }) {
      return this.getCustomHolidayLabels(cell) || [];
    },
    goPrev() {
      this.viewDate = addMonths(this.viewDate as any, -1) as any;
    },
    goNext() {
      this.viewDate = addMonths(this.viewDate as any, 1) as any;
    },

    // プルダウン
    buildMonthOptions() {
      const now = new Date();
      const rangeYears = 3;
      const startYear = now.getFullYear() - rangeYears;
      const endYear = now.getFullYear() + rangeYears;
      const out: Array<{ value: string; label: string }> = [];
      for (let y = startYear; y <= endYear; y++) {
        for (let m = 1; m <= 12; m++) {
          out.push({
            value: `${y}-${String(m).padStart(2, '0')}`,
            label: `${y}年 ${m}月`,
          });
        }
      }
      this.monthOptions = out;
    },

    // クリック処理
    emitDayClick(date: Date) {
      // console.log("emitDayClick", date);
      this.$emit('day-click', date);
    },
    emitEventClick(event: CalEvent) {
      // console.log("emitEventClick", event);
      this.$emit('event-click', event);
    },
    isSameDate,
    measureItemHeight(cell: HTMLElement): number {
      const probe = document.createElement('div');
      probe.className = 'cal-event';
      // scoped スタイルの適用対象にする
      const scopeId = (this as any).$options
        ? (this as any).$options._scopeId
        : undefined;
      if (scopeId) {
        probe.setAttribute(scopeId, '');
      }

      // 念のためインラインでも必要最小限を指定（scopedIDが付与されていない時の為）
      probe.style.visibility = 'hidden';
      probe.style.position = 'absolute';
      probe.style.pointerEvents = 'none';
      probe.style.marginTop = '2px';
      probe.style.marginBottom = '2px';
      probe.style.lineHeight = '1.4';
      probe.textContent = 'probe';
      cell.appendChild(probe);

      const rect = probe.getBoundingClientRect();
      const style = getComputedStyle(probe);
      const marginV =
        parseFloat(style.marginTop || '0') +
        parseFloat(style.marginBottom || '0');
      const itemH = rect.height + marginV;

      cell.removeChild(probe);
      return itemH || 20;
    },
    // セルの高さから 1セルあたりの表示行数を動的計算
    recalcMaxRows() {
      const grid = this.$refs.bodyGrid as HTMLElement | undefined;
      if (!grid) {
        return;
      }
      const cell = grid.querySelector('.cal-cell') as HTMLElement | null;
      if (!cell) {
        return;
      }

      const cellStyles = getComputedStyle(cell);
      const cellH = cell.clientHeight;

      // 日付表示の高さ（右上の数字）
      const dateEl = cell.querySelector('.cal-date') as HTMLElement | null;
      const dateH = dateEl ? dateEl.getBoundingClientRect().height : 16;

      // イベント1行の実効高さ（line-height + margin上下）
      // .cal-event { font-size:12px; line-height:1.4; margin:2px 0; }
      // 高さを取得する為、計測用に一つ取得
      const itemH = this.measureItemHeight(cell); // ←必ず実測

      const paddingTop = parseFloat(cellStyles.paddingTop || '4');
      const paddingBottom = parseFloat(cellStyles.paddingBottom || '4');

      const usable = cellH - dateH - paddingTop - paddingBottom;
      const rows = Math.max(1, Math.floor((usable + 0.5) / itemH)); // 誤差吸収
      this.maxRowsPerCell = rows;
    },

    // ▼ 並べ替え & 他◯件
    getDisplayRows(dayEvents: any[], maxRows: number) {
      const base = Array.isArray(dayEvents) ? dayEvents : [];

      // ▼ ここでカスタム休日をイベント欄から除外（日付欄だけで表示するため）
      const events = base.filter(e => {
        return !(e && e.isCustomHoliday === true);
      });

      // 1) 終日系の単独表示ルール
      const hasAbsence = events.some(
        e => e.kind === LaborCalendarEventKind.ABSENCE,
      );
      if (hasAbsence) {
        const absenceEvent = events.find(e => e.kind === LaborCalendarEventKind.ABSENCE);
        return [{ ...absenceEvent, displayText: buildDisplayText(absenceEvent), __type: 'event' }];
      }
      const hasHolidayFull = events.some(
        e => e.kind === LaborCalendarEventKind.HOLIDAY_FULL,
      );
      if (hasHolidayFull) {
        const holidayFullEvent = events.find(
          e => e.kind === LaborCalendarEventKind.HOLIDAY_FULL,
        );
        return [{ ...holidayFullEvent, displayText: buildDisplayText(holidayFullEvent), __type: 'event' }];
      }

      // 2) 並べ替え：遅刻 → （業務＋休暇半日：時刻昇順） → 早退
      const late = events
        .filter(e => e.kind === LaborCalendarEventKind.LATE)
        .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));

      const middle = events
        .filter(
          e =>
            e.kind === LaborCalendarEventKind.WORK ||
            e.kind === LaborCalendarEventKind.HOLIDAY_HALF,
        )
        .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));

      const early = events
        .filter(e => e.kind === LaborCalendarEventKind.EARLY)
        .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));

      const ordered = [...late, ...middle, ...early].map(e => ({
        ...e,
        displayText: buildDisplayText(e),
      }));
      const total = ordered.length;

      if (total <= maxRows) {
        return ordered.map(e => ({ ...e, __type: 'event' }));
      }

      if (maxRows === 1) {
        return [
          { ...ordered[0], __type: 'event' },
          { __k: `more-${total - 1}`, __type: 'more', rest: total - 1 },
        ];
      }

      // 最下段は「他◯件」専用に確保。実際にイベント本文を置ける行は maxRows - 1 行
      const slots = maxRows - 1;
      const visible = ordered.slice(0, slots);
      const hiddenCount = total - visible.length;

      return [
        ...visible.map(e => ({ ...e, __type: 'event' })),
        { __k: `more-${hiddenCount}`, __type: 'more', rest: hiddenCount },
      ];
    },

    emitMoreClick(date: Date, events: any[]) {
      // 必要ならモーダル/ドロワーで全件表示
      this.$emit('more-click', { date, events });
    },
    getCustomHolidayLabels(cell: { date: Date; events: any[] }) {
      const events = cell && Array.isArray(cell.events) ? cell.events : [];
      return events
        .filter(e => e && e.isCustomHoliday === true)
        .map(e => {
          const rawValue = e && e.workContent; // 0 も来る可能性
          const label = rawValue == null ? '' : String(rawValue).trim(); // 0→'0'
          return label;
        })
        .filter(label => label.length > 0); // 空だけ除外
    },
    // 日付行を赤くするかどうか
    hasCustomHoliday(cell: { date: Date; events: any[] }) {
      return this.getCustomHolidayLabels(cell).length > 0;
    },

    emitRangeChange() {
      const days = getMonthMatrix(this.viewDate as any);
      if (!days.length) {
        return;
      }

      const start = startOfDay(days[0]);
      const endInclusive = startOfDay(days[days.length - 1]);
      const endExclusive = addDays(endInclusive, 1); // 排他

      this.$emit('range-change', {
        start, // Date
        endExclusive,
        month: this.viewDate,
      });
    },
  },
});
